import { useMemo } from 'react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

const COLORS = [
  'hsl(220, 70%, 50%)',
  'hsl(160, 60%, 45%)',
  'hsl(30, 80%, 55%)',
  'hsl(0, 72%, 51%)',
  'hsl(270, 60%, 55%)',
  'hsl(190, 70%, 45%)',
  'hsl(45, 90%, 50%)',
  'hsl(330, 65%, 50%)',
  'hsl(120, 50%, 40%)',
  'hsl(200, 80%, 60%)',
  'hsl(15, 75%, 50%)',
  'hsl(240, 55%, 55%)',
  'hsl(80, 60%, 45%)',
  'hsl(300, 50%, 50%)',
  'hsl(170, 65%, 40%)'
];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) return null;

  return (
    <div className="rounded-md border bg-popover p-3 text-popover-foreground shadow-md max-w-xs">
      <p className="mb-2 text-sm font-medium">{label}</p>
      {payload
        .filter((entry) => entry.value > 0)
        .sort((a, b) => b.value - a.value)
        .map((entry) => (
          <div key={entry.name} className="flex items-center justify-between gap-4 text-xs">
            <div className="flex items-center gap-1.5">
              <div className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: entry.color }} />
              <span className="text-muted-foreground">{entry.name}</span>
            </div>
            <span className="font-medium tabular-nums">${Number(entry.value).toFixed(2)}</span>
          </div>
        ))}
    </div>
  );
};

const UsageChart = ({ timeSeries }) => {
  const { chartData, companies } = useMemo(() => {
    const dateMap = {};
    const companySet = new Set();

    timeSeries.forEach((row) => {
      const dateStr = typeof row.date === 'string' ? row.date.split('T')[0] : new Date(row.date).toISOString().split('T')[0];
      if (!dateMap[dateStr]) {
        dateMap[dateStr] = { date: dateStr };
      }
      dateMap[dateStr][row.company_name] = Number(row.daily_cost);
      companySet.add(row.company_name);
    });

    const sortedData = Object.values(dateMap).sort((a, b) => a.date.localeCompare(b.date));
    return { chartData: sortedData, companies: Array.from(companySet) };
  }, [timeSeries]);

  if (chartData.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Daily Cost by Company</CardTitle>
      </CardHeader>
      <CardContent>
        <div style={{ height: 400 }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 10, right: 20, bottom: 0, left: 0 }}>
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} tickFormatter={(v) => `$${v}`} />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {companies.map((company, i) => (
                <Area key={company} type="monotone" dataKey={company} stackId="1" stroke={COLORS[i % COLORS.length]} fill={COLORS[i % COLORS.length]} fillOpacity={0.6} />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};

export default UsageChart;
